/**
 * Phase 7 — Default Avatar. Renders the first letter of the User's handle on a deterministically-
 * derived color (handle hash → HSL hue). No server compute; no stored asset; falls out of
 * `AvatarImage` automatically when `avatarUrl` is null.
 *
 * <p>Color derivation uses a tiny string-hash → HSL conversion. Saturation 60% / lightness 55%
 * keep the palette legible for white text in both light and dark themes.
 */
interface InitialsAvatarProps {
  handle: string
  size: number
}

const hashString = (s: string): number => {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

const hueFromHandle = (handle: string): number => hashString(handle) % 360

const initialFromHandle = (handle: string): string => (handle[0] ?? '?').toUpperCase()

export const InitialsAvatar = ({ handle, size }: InitialsAvatarProps) => {
  const hue = hueFromHandle(handle)
  const initial = initialFromHandle(handle)
  return (
    <div
      aria-label={`Default avatar for @${handle}`}
      role="img"
      style={{
        width: size,
        height: size,
        backgroundColor: `hsl(${hue}, 60%, 55%)`,
        fontSize: Math.round(size * 0.5),
        lineHeight: 1,
      }}
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
    >
      {initial}
    </div>
  )
}
